import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/modern/utils";
import { TabEmpty, UsersPeriodSelect } from "../users-ui";
import { useUsersContext } from "../../context/UsersContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import type { ISwivuProject } from "@/types/SwivuProject";
import {
  USERS_HERO_METRIC_CLS,
  USERS_META_CLS,
  USERS_SUBTITLE_CLS,
  USERS_TITLE_CLS,
} from "../../constants/typography";

/** Augments ISwivuProject with stores that are present at runtime but not yet in the TypeScript type. */
type ProjectWithSchedule = ISwivuProject & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assignmentStore: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventStore: any;
};

function formatHours(n: number) {
  return n % 1 === 0 ? `${n}h` : `${n.toFixed(1)}h`;
}

// Status is derived from dates — we don't hardcode status strings
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deriveStatus(task: any): "active" | "done" | "upcoming" {
  const now = Date.now();
  const start = task.startDate ? new Date(task.startDate).getTime() : null;
  const end = task.endDate ? new Date(task.endDate).getTime() : null;
  if (end && now > end) return "done";
  if (start && start <= now) return "active";
  return "upcoming";
}

const STATUS_META = {
  active: { label: "Active", rail: "bg-primary", text: "text-primary" },
  done: { label: "Done", rail: "bg-success", text: "text-success" },
  upcoming: { label: "Upcoming", rail: "bg-muted-foreground/40", text: "text-muted-foreground" },
} as const;

type Range = "today" | "week" | "month" | "year" | "all";

const TASK_RANGE_OPTIONS: { value: Range; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "year", label: "This year" },
  { value: "all", label: "All time" },
];

function rangeWindow(r: Range): { start: Date; end: Date } {
  const now = new Date();
  if (r === "today") {
    const s = new Date(now); s.setHours(0, 0, 0, 0);
    const e = new Date(now); e.setHours(23, 59, 59, 999);
    return { start: s, end: e };
  }
  if (r === "week") {
    const day = now.getDay();
    const s = new Date(now); s.setDate(now.getDate() - day); s.setHours(0, 0, 0, 0);
    const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23, 59, 59, 999);
    return { start: s, end: e };
  }
  if (r === "month") {
    const s = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
    const e = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start: s, end: e };
  }
  if (r === "year") {
    const s = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
    const e = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
    return { start: s, end: e };
  }
  return { start: new Date(0), end: new Date(8_640_000_000_000_000) };
}

interface DerivedEvent {
  id: string | number;
  startDate: Date | null;
  endDate: Date | null;
  hoursSpent: number;
}

interface DerivedTask {
  id: string | number;
  name: string;
  color: string | null;
  status: "active" | "done" | "upcoming";
  startDate: Date | null;
  endDate: Date | null;
  totalHours: number;
  events: DerivedEvent[];
}

function ScheduleDateRow({ event }: { event: DerivedEvent }) {
  const start = event.startDate;
  const end = event.endDate;
  const startLabel = start
    ? start.toLocaleString(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : "—";
  const endLabel = end
    ? end.toLocaleString(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : "—";

  return (
    <li className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
      <div className="min-w-0 flex-1 flex items-center gap-2 text-sm text-foreground">
        <span className="truncate tabular-nums">{startLabel}</span>
        <span className="text-muted-foreground/50 shrink-0" aria-hidden>→</span>
        <span className="truncate tabular-nums">{endLabel}</span>
      </div>
      <span className="tabular-nums text-base font-medium text-foreground shrink-0">
        {formatHours(event.hoursSpent)}
      </span>
    </li>
  );
}

function TaskRow({ task }: { task: DerivedTask }) {
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[task.status];
  const canExpand = task.events.length > 0;
  const scheduleLabel = task.startDate
    ? task.startDate.toLocaleString(undefined, {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : "—";

  // Use the task's own color when available; fall back to the status-derived class
  const railColor = task.color;

  return (
    <div
      className={cn(
        "relative rounded-lg border overflow-hidden transition-colors duration-150",
        open
          ? "border-border bg-card shadow-sm"
          : "border-border/70 bg-card hover:border-border hover:bg-muted/20",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full",
          !railColor && meta.rail,
        )}
        style={railColor ? { backgroundColor: railColor } : undefined}
      />
      <button
        type="button"
        onClick={() => canExpand && setOpen((v) => !v)}
        disabled={!canExpand}
        className={cn(
          "w-full text-left pl-5 pr-5 py-4",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
          !canExpand && "cursor-default",
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1.5">
            <p className={cn(USERS_TITLE_CLS, "font-normal truncate leading-none")}>{task.name}</p>
            <p className={cn("tabular-nums truncate", USERS_SUBTITLE_CLS)}>{scheduleLabel}</p>
          </div>
          <div className="shrink-0 text-right space-y-1.5">
            <p className={cn(USERS_TITLE_CLS, "tabular-nums leading-none")}>
              {formatHours(task.totalHours)}
            </p>
            <p className={cn("font-normal", USERS_META_CLS, meta.text)}>{meta.label}</p>
          </div>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && canExpand && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-border/50 mx-3 mb-2.5 pt-2.5">
              <p className={cn(USERS_SUBTITLE_CLS, "mb-1.5 px-0.5")}>Schedule dates</p>
              <ul className="space-y-1.5">
                {task.events.map((e) => (
                  <ScheduleDateRow key={e.id} event={e} />
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function TasksTab({ user }: { user: any }) {
  const { project } = useUsersContext();
  const [range, setRange] = useState<Range>("week");

  const { version: assignmentVersion } = useSwivuStore({ storeId: STORE_KEYS.assignmentStore });
  const { version: eventVersion } = useSwivuStore({ storeId: STORE_KEYS.eventStore });
  const { version: resourceVersion } = useSwivuStore({ storeId: STORE_KEYS.resourceStore });
  const { version: taskVersion } = useSwivuStore({ storeId: STORE_KEYS.taskStore });

  const { start: rangeStart, end: rangeEnd } = rangeWindow(range);

  const tasks = useMemo<DerivedTask[]>(() => {
    if (!project) return [];
    const { assignmentStore: aStore, eventStore: eStore } = project as unknown as ProjectWithSchedule;
    const tStore = project.extraTaskStore;
    if (!aStore || !eStore) return [];

    const userResourceIds = new Set<string | number>(
      project.resourceStore
        .query((r) => ((r as any).assignedBoardUsers ?? []).includes(user.id))
        .map((r) => r.id),
    );
    if (userResourceIds.size === 0) return [];

    const eventToTask = new Map<string | number, any>();
    for (const task of (tStore.query(() => true) as any[])) {
      for (const evt of (task.events ?? []) as any[]) {
        if (evt?.id != null) eventToTask.set(evt.id, task);
      }
    }

    const groupMap = new Map<
      string | number,
      { events: any[]; eventIds: Set<string | number>; task: any | null; evt0: any }
    >();
    for (const asgn of aStore.records as any[]) {
      if (!userResourceIds.has(asgn.resourceId)) continue;
      const evt = asgn.event ?? eStore.getById?.(asgn.eventId);
      if (!evt || evt.archived) continue;

      const task = eventToTask.get(evt.id)
        ?? (evt.taskId != null ? (tStore.getById(evt.taskId) ?? null) : null);

      const groupKey = task != null ? task.id : `evt-${evt.id}`;

      const existing = groupMap.get(groupKey);
      if (existing) {
        if (!existing.eventIds.has(evt.id)) {
          existing.eventIds.add(evt.id);
          existing.events.push(evt);
        }
      } else {
        groupMap.set(groupKey, { events: [evt], eventIds: new Set([evt.id]), task, evt0: evt });
      }
    }

    // Filter to selected range and build DerivedTask list
    const result: DerivedTask[] = [];
    for (const [, group] of groupMap) {
      const { events, task, evt0 } = group;

      const rangeEvents = events.filter((evt: any) => {
        const s = evt.startDate ? new Date(evt.startDate) : null;
        const e = evt.endDate ? new Date(evt.endDate) : null;
        if (!s && !e) return true;
        const evtStart = s?.getTime() ?? 0;
        const evtEnd = e?.getTime() ?? evtStart;
        // Half-open [start, end): exclusive end matches Bryntum bars (midnight end = prior day only).
        return evtStart < rangeEnd.getTime() && evtEnd > rangeStart.getTime();
      });
      if (rangeEvents.length === 0) continue;

      // Resolve name: task name → event name → generic fallback
      const name: string =
        (task?.name as string | undefined) ||
        (evt0.name as string | undefined) ||
        `Event #${evt0.id}`;

      // Resolve color from the task record (Task.color getter always returns a value)
      const color: string | null = task != null ? (task.color as string) : null;

      const sortedByStart = [...rangeEvents].sort((a: any, b: any) => {
        return (a.startDate ? new Date(a.startDate).getTime() : 0)
          - (b.startDate ? new Date(b.startDate).getTime() : 0);
      });
      const firstEvt = sortedByStart[0];
      const lastEvt = sortedByStart[sortedByStart.length - 1];
      const taskStart = firstEvt.startDate ? new Date(firstEvt.startDate) : null;
      const taskEnd = lastEvt.endDate ? new Date(lastEvt.endDate) : null;

      const statusInput = {
        startDate: (task?.startDate ?? firstEvt.startDate) as unknown,
        endDate: (task?.endDate ?? lastEvt.endDate) as unknown,
      };
      const status = deriveStatus(statusInput);

      const derivedEvents: DerivedEvent[] = rangeEvents.map((evt: any) => {
        const s = evt.startDate ? new Date(evt.startDate) : null;
        const e = evt.endDate ? new Date(evt.endDate) : null;
        const hrs = s && e ? (e.getTime() - s.getTime()) / 3_600_000 : 0;
        return { id: evt.id, startDate: s, endDate: e, hoursSpent: Math.round(hrs * 10) / 10 };
      });

      const totalHours = derivedEvents.reduce((sum, e) => sum + e.hoursSpent, 0);
      result.push({
        id: task?.id ?? evt0.id,
        name,
        color,
        status,
        startDate: taskStart,
        endDate: taskEnd,
        totalHours: Math.round(totalHours * 10) / 10,
        events: derivedEvents.sort(
          (a, b) => (a.startDate?.getTime() ?? 0) - (b.startDate?.getTime() ?? 0),
        ),
      });
    }

    return result.sort((a, b) => (a.startDate?.getTime() ?? 0) - (b.startDate?.getTime() ?? 0));
  }, [user.id, assignmentVersion, eventVersion, resourceVersion, taskVersion, project, rangeStart.getTime(), rangeEnd.getTime()]);

  const scheduled = tasks.reduce((s, t) => s + t.totalHours, 0);

  const rangeLabel =
    range === "today" ? "Scheduled today"
    : range === "week" ? "Scheduled this week"
    : range === "month" ? "Scheduled this month"
    : range === "year" ? "Scheduled this year"
    : "Scheduled all time";

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-border/70 bg-card px-4 py-3.5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className={cn(USERS_META_CLS, "font-normal text-muted-foreground tracking-wide")}>
              {rangeLabel}
            </p>
            <div className="mt-2 flex items-end gap-2.5 flex-wrap">
              <span className={cn(USERS_HERO_METRIC_CLS, "text-foreground")}>
                {formatHours(scheduled)}
              </span>
            </div>
          </div>

          <UsersPeriodSelect
            value={range}
            onValueChange={setRange}
            options={TASK_RANGE_OPTIONS}
            triggerClassName="w-[8.5rem]"
          />
        </div>
      </div>

      <div className="space-y-2.5">
        {tasks.length === 0 ? (
          <TabEmpty
            icon="fa-light fa-swap-arrows"
            message={
              user.isDeactivated
                ? "No tasks scheduled for this user."
                : "No tasks scheduled in this period."
            }
            hint={
              user.isDeactivated
                ? undefined
                : "Try a different time range, or assign events to this user via the scheduler."
            }
          />
        ) : (
          tasks.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
            >
              <TaskRow task={t} />
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
